import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { RoleFamily, Permission } from '@school-erp/types';
import { Spinner } from '@school-erp/ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: RoleFamily[];
  permissions?: Permission[];
}

export function ProtectedRoute({ children, roles, permissions }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If both roles and permissions are specified, grant access if EITHER matches (OR logic).
  // If only roles is specified, check roles. If only permissions, check permissions.
  const hasRole = roles && roles.length > 0 ? user.roles.some((r) => roles.includes(r)) : undefined;
  const hasPermission = permissions && permissions.length > 0 ? permissions.some((p) => user.permissions.includes(p)) : undefined;

  const roleSpecified = hasRole !== undefined;
  const permSpecified = hasPermission !== undefined;

  let accessGranted = true;
  if (roleSpecified && permSpecified) {
    // OR logic: either role or permission is sufficient
    accessGranted = hasRole || hasPermission;
  } else if (roleSpecified) {
    accessGranted = hasRole;
  } else if (permSpecified) {
    accessGranted = hasPermission;
  }

  if (!accessGranted) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-500">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
